import { useRef, useEffect, useCallback } from 'react';

/**
 * Custom React hook that returns a debounced version of a callback function.
 * The debounced function delays invoking the callback until after the specified delay
 * has elapsed since the last time it was called. Also provides methods to cancel or flush the debounce.
 *
 * @param {Function} callback - The function to debounce.
 * @param {number} delay - The delay in milliseconds.
 * @returns {{
 *   call: Function,   // The debounced function to call.
 *   cancel: Function, // Cancels any pending invocation.
 *   flush: Function   // Immediately invokes the callback if pending.
 * }}
 *
 * @example
 * const { call, cancel, flush } = useDebouncedCallback(myCallback, 300);
 * call(arg1, arg2); // Will debounce myCallback
 * cancel(); // Cancels pending callback
 * flush(); // Immediately invokes pending callback
 */
export function useDebouncedCallback(callback, delay = 300) {
  const callbackRef = useRef(callback);
  const timerRef = useRef(null);
  const lastArgsRef = useRef([]);

  // Keep latest callback reference
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const debouncedFn = useCallback(
    (...args) => {
      lastArgsRef.current = args;

      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timerRef.current = setTimeout(() => {
        callbackRef.current(...lastArgsRef.current);
        timerRef.current = null;
      }, delay);
    },
    [delay],
  );

  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const flush = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      callbackRef.current(...lastArgsRef.current);
      timerRef.current = null;
    }
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  // Return debounced function and controls
  return {
    call: debouncedFn,
    cancel,
    flush,
  };
}
